import { Line } from "@ant-design/plots";
import { Button, Card, Col, DatePicker, message, Row, Space, Spin, Table } from "antd";
import { LINE_WISE_DAILY_CPM } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import ListTable from "common/ListTable";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { formatNumberWithCommas } from "utils/functions";

export default function CPM() {
  const [selectedDate, setSelectedDate] = useState(moment());
  const [data, setData] = useState({
    dataSource: [],
    barChart: [],
    loading: false,
  });
  const [hasSearched, setHasSearched] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Fix round 1: guard against setState after unmount.
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const config = {
    data: data.barChart,
    xField: "line",
    yField: "cpm",
    point: {
      shapeField: "square",
      sizeField: 4,
    },
    interaction: {
      tooltip: {
        marker: false,
      },
    },
    style: {
      lineWidth: 2,
    },
  };

  const columns = [
    {
      title: "Sewing Line",
      dataIndex: "line",
      key: "line",
    },
    {
      title: "Line Chief",
      dataIndex: "line_chief",
      key: "line_chief",
      render: (chiefs) =>
        chiefs
          ?.map((c) => c.line_chief)
          .filter(Boolean)
          .join(", "),
    },
    {
      title: "Line Attendance",
      dataIndex: "line_attendance",
      key: "line_attendance",
      align: "right",
    },
    {
      title: "Cost",
      dataIndex: "cost",
      key: "cost",
      render: (value) => formatNumberWithCommas(value.toFixed(2)),
      align: "right",
    },
    {
      title: "Production",
      dataIndex: "today_production",
      key: "today_production",
      align: "right",
      render: (value) => parseInt(value).toLocaleString(undefined, { maximumFractionDigits: 0 }),
    },
    {
      title: "SAM",
      dataIndex: "sam",
      key: "sam",
      align: "right",
      render: (value) => formatNumberWithCommas(value.toFixed(2)),
    },
    {
      title: "Earn Minutes",
      dataIndex: "earn_minute",
      key: "earn_minute",
      align: "right",
      render: (value) => formatNumberWithCommas(value),
    },
    {
      title: "CPM",
      dataIndex: "cpm",
      key: "cpm",
      align: "right",
      render: (value) => value.toFixed(3),
    },
    {
      title: "Ranking on CPM",
      dataIndex: "rank",
      key: "rank",
    },
  ];

  // ux2(A/presets): dateOverride lets the Today/Yesterday buttons fetch with
  // the just-picked date instead of the stale `selectedDate` closure value.
  const handleSearch = async (dateOverride) => {
    const searchDate = dateOverride || selectedDate;
    setData({
      dataSource: [],
      barChart: [],
      loading: true,
    });
    setHasSearched(true);
    setFetchError(null);
    try {
      const body = {
        start_date: searchDate.format("YYYY-MM-DD"),
      };
      const query = `${LINE_WISE_DAILY_CPM}`;
      const response = await getData(query, false, body);

      if (!mountedRef.current) return;

      if (response?.data?.code === 200) {
        setData({
          dataSource: response?.data?.data?.data.sort((a, b) => {
            const numA = parseInt(a.line.match(/\d+/));
            const numB = parseInt(b.line.match(/\d+/));
            return numA - numB;
          }),
          barChart: response?.data?.data?.data.map((d) => ({
            line: d.line,
            cpm: d.cpm,
          })),
          loading: false,
        });
      } else {
        setData({
          dataSource: [],
          barChart: [],
          loading: false,
        });
        setFetchError("Failed to fetch data");
        message.error("Failed to fetch data");
      }
    } catch (error) {
      if (!mountedRef.current) return;
      setData({
        dataSource: [],
        barChart: [],
        loading: false,
      });
      setFetchError("An error occurred while fetching data");
      message.error("An error occurred while fetching data");
    }
  };

  const handleReset = () => {
    setSelectedDate(moment());
    setData({
      dataSource: [],
      barChart: [],
      loading: false,
    });
    setHasSearched(false);
  };

  // Round 4 (T2): restored. Round 3 removed this because line-wise-daily-cpm measured
  // 184.10s warm (16.71s cold) for today's date on a real data day
  // (T1-timings/timings.md row 22). Task 1's set-based rewrite of
  // LineWiseDailyCpmService now answers the same seeded default in 0.03-0.55s
  // in-process and 0.31-0.32s kernel-dispatch
  // (D:\cgl-erp\patches\ux-round4\golden\timings.md), comfortably inside R4-5's
  // 5s-warm/15s-cold bar, so the mount-time fetch is safe again.
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Card
      title={`
        Line wise Daily CPM Report as on ${selectedDate.format("DD-MMM-YYYY")}
      `}
      bordered={false}
    >
      <br />
      <Row gutter={24}>
        <Col span={6}>
          <DatePicker
            allowClear={false}
            format={"DD-MMM-YYYY"}
            style={{
              width: "100%",
            }}
            value={selectedDate}
            onChange={handleDateChange}
          />
        </Col>
        <Col span={6}>
          <Space>
            <Button
              size="small"
              onClick={() => {
                const d = moment();
                setSelectedDate(d);
                handleSearch(d);
              }}
              disabled={data.loading}
            >
              Today
            </Button>
            <Button
              size="small"
              onClick={() => {
                const d = moment().subtract(1, "days");
                setSelectedDate(d);
                handleSearch(d);
              }}
              disabled={data.loading}
            >
              Yesterday
            </Button>
            <Button type="primary" danger onClick={handleReset} disabled={data.loading}>
              Reset
            </Button>
            <Button type="primary" onClick={() => handleSearch()} loading={data.loading} disabled={data.loading}>
              Search
            </Button>
          </Space>
        </Col>
      </Row>

      {hasSearched && (
        <>
          <br />
          <ListTable
            dataSource={data.dataSource}
            columns={columns}
            pagination={false}
            bordered
            loading={data.loading}
            error={fetchError}
            onRetry={() => handleSearch()}
            locale={{
              emptyText: `No records for ${selectedDate.format("DD-MMM-YYYY")}. Change the date above.`,
            }}
            summary={(tableData) => {
              let totalCost = 0;
              let totalProduction = 0;
              let totalEarnMinutes = 0;
              let totalCPM = 0;
              tableData.forEach(({ cost, today_production, earn_minute, cpm }) => {
                totalCost += parseFloat(cost);
                totalProduction += parseFloat(today_production);
                totalEarnMinutes += parseFloat(earn_minute);
                totalCPM += parseFloat(cpm);
              });

              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                  <Table.Summary.Cell index={1}></Table.Summary.Cell>
                  <Table.Summary.Cell index={2}></Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    {formatNumberWithCommas(totalCost.toFixed(2))}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="right">
                    {totalProduction}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={5}></Table.Summary.Cell>
                  <Table.Summary.Cell index={6} align="right">
                    {totalEarnMinutes.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={7} align="right">
                    {totalCPM.toFixed(3)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={8}></Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
          <div
            style={{
              padding: "20px",
              backgroundColor: "#ffffff",
              borderRadius: "10px",
            }}
          >
            <h3
              style={{
                color: "#00bfff",
                textAlign: "center",
                marginBottom: "20px",
              }}
            >
              Today's Est. Net Profit/taka
            </h3>
            <Line {...config} />
          </div>
        </>
      )}
    </Card>
  );
}
